/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * One portal-settings fetch per settings page, shared by two readers.
 *
 * The pending band needs the portal state to know whether a connected portal
 * has its sending switched off, and the Portais section needs the same state
 * to draw the switches. Letting each call `usePortalSettings` would issue the
 * same GET twice on every page load, and would let the band and the section
 * disagree for as long as the two responses differ.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { usePortalSettings } from '../components/PortalSettings/usePortalSettings';

type PortalSettingsValue = ReturnType<typeof usePortalSettings>;

const PortalSettingsContext = createContext<PortalSettingsValue | null>(null);

/**
 * Fetches the agency's portal settings once and shares them.
 *
 * @param props - Component props.
 * @param props.children - The settings surface.
 * @returns The provider element.
 */
export function PortalSettingsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const value = usePortalSettings(agency?.id, t);
  return (
    <PortalSettingsContext.Provider value={value}>
      {children}
    </PortalSettingsContext.Provider>
  );
}

/**
 * Reads the shared portal settings.
 *
 * @returns The portal settings, flags and mutation actions.
 * @throws When called outside {@link PortalSettingsProvider}.
 */
export function usePortalSettingsContext(): PortalSettingsValue {
  const value = useContext(PortalSettingsContext);
  if (!value) throw new Error('usePortalSettingsContext requires PortalSettingsProvider');
  return value;
}

/**
 * Reads the shared portal settings without requiring the provider.
 *
 * The pending band is built at page level, where the provider is present,
 * but this keeps the band usable from a story or a test that renders it bare.
 *
 * @returns The portal settings, or null when there is no provider.
 */
export function usePortalSettingsOptional(): PortalSettingsValue | null {
  return useContext(PortalSettingsContext);
}
